import { Point, Wall, FloorPlanElement, DoorElement } from '../types';
import { distanceToLine } from './floorPlanUtils';
import { SNAP_THRESHOLD, WALL_THICKNESS } from '../constants/floorPlanConstants';

const DEFAULT_DOOR_WIDTH = 50; // Default width for ESTATE mode
const SMALL_DOOR_WIDTH = 40; // Smaller size for Retrofit/Energy mode (50% reduction)

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return start;
    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

export function findWallForDoor(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
    let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
    let minDistance = Infinity;

    for (const wall of walls) {
        const distance = distanceToLine(point, wall.start, wall.end);
        const closestPoint = getClosestPointOnSegment(point, wall.start, wall.end);
        const isOnSegment = Math.hypot(closestPoint.x - point.x, closestPoint.y - point.y) < SNAP_THRESHOLD * 3;

        if (distance < SNAP_THRESHOLD * 1.5 && isOnSegment) {
            if (distance < minDistance) {
                minDistance = distance;
                clickedWall = wall;
            }
        }
    }
    return clickedWall;
}

export function createDoorElement(
    point: Point,
    wall: Wall & { tempId:string, stableId: string },
    currentFloor: number,
    useSmallSize: boolean = false
): DoorElement | null {
    const doorWidth = useSmallSize ? SMALL_DOOR_WIDTH : DEFAULT_DOOR_WIDTH;
    console.log('🚪 createDoorElement:', { useSmallSize, doorWidth, SMALL_DOOR_WIDTH, DEFAULT_DOOR_WIDTH });
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength < doorWidth) return null;

    const closestPoint = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromStart = Math.hypot(closestPoint.x - wall.start.x, closestPoint.y - wall.start.y);

    let positionOnWall = distFromStart / wallLength;

    const halfDoorWidthOnWall = (doorWidth / 2) / wallLength;
    positionOnWall = Math.max(halfDoorWidthOnWall, Math.min(positionOnWall, 1 - halfDoorWidthOnWall));

    const doorCenter = {
        x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
        y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
    };

    // Determine which side of the wall the cursor is on
    const wallDx = wall.end.x - wall.start.x;
    const wallDy = wall.end.y - wall.start.y;
    const pointDx = point.x - wall.start.x;
    const pointDy = point.y - wall.start.y;
    
    // Using 2D cross-product to determine the side
    const crossProduct = wallDx * pointDy - wallDy * pointDx;
    const opensIn = crossProduct < 0;

    const newDoor: DoorElement = {
        id: `door-preview-${Date.now()}`,
        type: 'door',
        wallId: wall.stableId,
        positionOnWall,
        width: DEFAULT_DOOR_WIDTH,
        swingRight: true,
        opensIn: opensIn,
        floor: currentFloor,
        position: doorCenter,
        rotation: 0,
    };
    
    return newDoor;
}

function drawSingleDoor(ctx: CanvasRenderingContext2D, door: DoorElement, wall: Wall, scale: number, viewOffset: Point) {
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.hypot(wallVector.x, wallVector.y);
    if (wallLength === 0) return;

    const wallAngle = Math.atan2(wallVector.y, wallVector.x);

    // Use door position directly since canvas context is already transformed
    const doorCenter = {
        x: door.position.x,
        y: door.position.y
    };
    const halfWidth = door.width / 2;
    const doorWidth = door.width;

    const wallDir = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };

    const p1 = { x: doorCenter.x - halfWidth * wallDir.x, y: doorCenter.y - halfWidth * wallDir.y };
    const p2 = { x: doorCenter.x + halfWidth * wallDir.x, y: doorCenter.y + halfWidth * wallDir.y };

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.strokeStyle = ctx.canvas.style.backgroundColor || '#ffffff';
    ctx.lineWidth = WALL_THICKNESS + (2 / scale);
    ctx.lineCap = 'butt';
    ctx.stroke();
    ctx.restore();

    const hinge = door.swingRight ? p2 : p1;
    
    const normalAngle = wallAngle + (door.opensIn ? -Math.PI / 2 : Math.PI / 2);
    
    let arcStartAngle, arcEndAngle, counterClockwise;

    if (door.swingRight) {
        if (door.opensIn) {
            arcStartAngle = wallAngle + Math.PI;
            arcEndAngle = normalAngle;
            counterClockwise = false;
        } else {
            arcStartAngle = wallAngle + Math.PI;
            arcEndAngle = normalAngle;
            counterClockwise = true;
        }
    } else {
        if (door.opensIn) {
            arcStartAngle = wallAngle;
            arcEndAngle = normalAngle;
            counterClockwise = true;
        } else {
            arcStartAngle = wallAngle;
            arcEndAngle = normalAngle;
            counterClockwise = false;
        }
    }

    const doorLeafEnd = {
        x: hinge.x + doorWidth * Math.cos(normalAngle),
        y: hinge.y + doorWidth * Math.sin(normalAngle),
    };

    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1.5 / scale;

    ctx.beginPath();
    ctx.moveTo(hinge.x, hinge.y);
    ctx.lineTo(doorLeafEnd.x, doorLeafEnd.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(hinge.x, hinge.y, doorWidth, arcStartAngle, arcEndAngle, counterClockwise);
    ctx.stroke();
}

export function drawDoors(
    ctx: CanvasRenderingContext2D,
    elements: FloorPlanElement[],
    walls: (Wall & { tempId: string, stableId: string })[],
    currentFloor: number,
    scale: number,
    viewOffset: Point
) {
    const doors = elements.filter((e: any) => e.type === 'door' && e.floor === currentFloor) as unknown as DoorElement[];

    for (const door of doors) {
        const wall = walls.find(w => w.stableId === door.wallId);
        if (wall) {
            drawSingleDoor(ctx, door, wall, scale, viewOffset);
        }
    }
}

export function drawDoorPreview(
    ctx: CanvasRenderingContext2D,
    doorPreview: DoorElement | null,
    walls: (Wall & { tempId: string, stableId: string })[],
    scale: number,
    viewOffset: Point
) {
    if (!doorPreview) return;
    const wall = walls.find(w => w.stableId === doorPreview.wallId);
    if (wall) {
        ctx.save();
        ctx.globalAlpha = 0.7;
        drawSingleDoor(ctx, doorPreview, wall, scale, viewOffset);
        ctx.restore();
    }
} 